import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { themeDark, themeLight } from './theme';

interface ThemeContextType {
  isDarkMode: boolean;
  setTheme: (theme: string) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Apply theme class to body element for CSS targeting
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const body = document.body;

      if (isDarkMode) {
        body.classList.add('dark');
        body.setAttribute('data-theme', 'dark');
      } else {
        body.classList.remove('dark');
        body.setAttribute('data-theme', 'light');
      }
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const savedTheme = localStorage.getItem('theme-preference');
    setIsDarkMode(savedTheme === 'dark');
  }, []);

  const setTheme = useCallback((theme: string) => {
    setIsDarkMode(theme === 'dark');
    localStorage.setItem('theme-preference', theme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(!isDarkMode ? 'dark' : 'light');
  }, [isDarkMode, setTheme]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, setTheme }}>
      <MuiThemeProvider theme={isDarkMode ? themeDark : themeLight}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
